import { relations, sql } from 'drizzle-orm';
import { check, index, integer, pgEnum, pgTable, serial, text, timestamp, uniqueIndex } from 'drizzle-orm/pg-core';
import { HashedTokenSecret, TokenPrefix, TokenSecret, TokenSeparator } from '../utils/token';
import { TypeId, typeIdDataType as typeId } from '../utils/typeid';
import { application } from './application';
import { accountScope } from './scope';

type AppId = TypeId<'app'>;

export type ApiKeyToken = `${TokenPrefix}${TokenSeparator}${AppId}${TokenSeparator}${TokenSecret}`;
export type HashedApiKeyToken = `${TokenPrefix}${TokenSeparator}${AppId}${TokenSeparator}${HashedTokenSecret}`;

export const API_KEY_ROLES = ['service', 'account'] as const;
export type ApiKeyRole = (typeof API_KEY_ROLES)[number];
export const apiKeyRoleEnum = pgEnum('api_key_role', API_KEY_ROLES);

export const apiKey = pgTable(
  'api_key',
  {
    id: serial().primaryKey(),
    public_id: typeId('apiKey', 'public_id').notNull(),
    prefix: text().notNull().$type<TokenPrefix>(),
    hashed_token: text().notNull().$type<HashedApiKeyToken>(),
    issued_at: timestamp({ withTimezone: true, mode: 'string' }).notNull().defaultNow(),
    expires_at: timestamp({ withTimezone: true, mode: 'string' }).notNull(),
    name: text().notNull(),
    role: apiKeyRoleEnum().notNull().default('service'),
    application_id: integer()
      .references(() => application.id, { onDelete: 'cascade' })
      .notNull(),
    account_scope_id: integer().references(() => accountScope.id, { onDelete: 'restrict' }),
  },
  (t) => [
    uniqueIndex('api_key_public_id_uq').on(t.public_id),
    uniqueIndex('api_key_prefix_uq').on(t.prefix),
    index('api_key_application_id_idx').on(t.application_id),
    index('api_key_account_scope_id_idx').on(t.account_scope_id),
    index('api_key_expires_at_idx').on(t.expires_at),
    check('api_key_service_scope_ck', sql`${t.role} <> 'service' OR ${t.account_scope_id} IS NULL`),
  ],
);

export const apiKeyRelations = relations(apiKey, ({ one }) => ({
  application: one(application, { fields: [apiKey.application_id], references: [application.id] }),
  accountScope: one(accountScope, { fields: [apiKey.account_scope_id], references: [accountScope.id] }),
}));
